import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy, limit, getDoc } from 'firebase/firestore';
import { VideoCameraOutlined, CloseOutlined } from '@ant-design/icons';

/**
 * VideoCallBanner
 * Fixed-top banner that appears when a video call starts for a CMT incident.
 * - Persistent until closed or joined
 * - Clicking anywhere except the X opens the join link
 * - Participates in global banner stacking via register/unregister hooks
 */
export default function VideoCallBanner({ bannerId = 'video-call-banner', registerBanner, unregisterBanner, calculatePosition }) {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const { activeCmt, availableCmts } = useMultiCmt();

  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [topOffset, setTopOffset] = useState(0);
  const seenKeysRef = React.useRef(new Set());

  const userCmtIds = useMemo(() => (availableCmts || []).map(c => c.id), [availableCmts]);

  // Subscribe to active org video_meeting notifications
  useEffect(() => {
    if (!db || !currentUser?.orgId) return;

    const ref = collection(db, 'c3_notifications');
    const q = query(
      ref,
      where('orgId', '==', currentUser.orgId),
      where('type', '==', 'video_meeting'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsub = onSnapshot(q, (snap) => {
      try { console.log('VideoCallBanner: notifications snapshot', snap.size); } catch {}
      // Pick the most recent eligible notification for this user
      const eligible = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => {
          // Restrict to this user's CMTs if targetCmtId is set
          if (n.targetCmtId && userCmtIds && userCmtIds.length > 0) {
            return userCmtIds.includes(n.targetCmtId);
          }
          return true;
        })
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      if (eligible.length > 0) {
        try { console.log('VideoCallBanner: eligible notification', eligible[0]); } catch {}
        setNotification(eligible[0]);
        setVisible(true);
      } else {
        setVisible(false);
        setNotification(null);
      }
    });

    return () => unsub();
  }, [db, currentUser?.orgId, userCmtIds.join('|')]);

  // Fallback: watch system messages in Live Updates for video start (link in text)
  useEffect(() => {
    if (!db || !currentUser?.orgId) return;

    const ref = collection(db, 'c3_incidentChats');
    const q = query(
      ref,
      where('orgId', '==', currentUser.orgId),
      orderBy('timestamp', 'desc'),
      limit(25)
    );

    const extractUrl = (text) => {
      if (!text) return null;
      const match = String(text).match(/https?:\/\/[^\s]+/);
      return match ? match[0] : null;
    };

    const unsub = onSnapshot(q, async (snap) => {
      try { console.log('VideoCallBanner: chat snapshot', snap.size); } catch {}
      for (const d of snap.docs) {
        const m = d.data();
        const key = `chat:${d.id}`;
        if (seenKeysRef.current.has(key)) continue;
        // Only system message with video conference hint
        if (m.authorId !== 'system') continue;
        const url = extractUrl(m.message);
        if (!url) continue;
        if (!/video\s*conference|video\s*call/i.test(m.message || '')) continue;

        // Load incident to filter by CMT
        try {
          if (!m.incidentId) continue;
          const incRef = doc(db, 'c3_incidents', m.incidentId);
          const incSnap = await getDoc(incRef);
          if (!incSnap.exists()) continue;
          const inc = incSnap.data();
          const targetCmtId = inc.cmtId || null;
          if (targetCmtId && userCmtIds && userCmtIds.length > 0 && !userCmtIds.includes(targetCmtId)) continue;

          seenKeysRef.current.add(key);
          setNotification({ id: key, joinUrl: url, message: 'Video call started', targetCmtId });
          setVisible(true);
          break;
        } catch (_) {
          // ignore
        }
      }
    });

    return () => unsub();
  }, [db, currentUser?.orgId, userCmtIds.join('|')]);

  // Register/unregister in stacking system
  useEffect(() => {
    if (visible && bannerId && registerBanner) {
      registerBanner(bannerId);
      const pos = calculatePosition ? calculatePosition(bannerId) : 0;
      setTopOffset(pos);
      return () => {
        if (unregisterBanner) unregisterBanner(bannerId);
      };
    } else if (!visible && bannerId && unregisterBanner) {
      unregisterBanner(bannerId);
    }
  }, [visible, bannerId, registerBanner, unregisterBanner, calculatePosition]);

  // Recompute top offset when stacking changes
  useEffect(() => {
    if (!visible) return;
    const pos = calculatePosition ? calculatePosition(bannerId) : 0;
    setTopOffset(pos);
  }, [visible, calculatePosition, bannerId]);

  const handleJoin = async () => {
    if (!notification?.joinUrl) return;
    try {
      window.open(notification.joinUrl, '_blank', 'noopener,noreferrer');
    } finally {
      // Mark as read on join
      try {
        await updateDoc(doc(db, 'c3_notifications', notification.id), { read: true });
      } catch {}
      setVisible(false);
    }
  };

  const handleDismiss = async (e) => {
    e.stopPropagation();
    try {
      if (notification?.id) {
        await updateDoc(doc(db, 'c3_notifications', notification.id), { read: true });
      }
    } catch {}
    setVisible(false);
  };

  if (!visible || !notification) return null;

  return (
    <div
      onClick={handleJoin}
      style={{
        position: 'fixed',
        top: `${topOffset}px`,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: '#2b6cb0',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
      }}
      title="Join video call"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <VideoCameraOutlined style={{ fontSize: 18 }} />
        <div style={{ fontWeight: 600 }}>
          {notification.message || 'Video call started'}
        </div>
      </div>
      <button
        onClick={handleDismiss}
        style={{
          background: 'transparent',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.6)',
          borderRadius: 4,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        aria-label="Dismiss video call banner"
      >
        <CloseOutlined />
      </button>
    </div>
  );
}


